import { FaultUpdate } from '@eagle/data-function-types';
import { Dialog, DialogContent, styled, SwipeableDrawer, Typography } from '@ethings/ui-toolkit';
import type { FC, ReactNode } from 'react';
import { evaluate } from '../../evaluator';
import { useSmallScreen, useUiTemplate } from '../../hooks';
import { HISTORY_FAULT_GRID_DEFAULT_TEMPLATE } from '../../ui-templates/history-fault-grid';
import { useEventDialog } from '../event-dialog';
import { MiddleSpinner } from '../middle-spinner';
import { FaultDescription, NormalizedFaultEventItem } from './journey-faults.utils';

interface JourneyFaultsDialogProps {
  item: NormalizedFaultEventItem;
  isOpen: boolean;
  onClose: VoidFunction;
}

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  '&.MuiDrawer-root > .MuiPaper-root': {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
}));

export const JourneyFaultsDialog: FC<JourneyFaultsDialogProps> = ({ item, isOpen, onClose }) => {
  const isSmallScreen = useSmallScreen();

  return (
    <>
      {isSmallScreen ? (
        <StyledSwipeableDrawer anchor="bottom" disableSwipeToOpen disableEnforceFocus open={isOpen} onOpen={() => { }} onClose={onClose}>
          <FaultDialogContent item={item} />
        </StyledSwipeableDrawer>
      ) : (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus>
          <FaultDialogContent item={item} />
        </Dialog>
      )}
    </>
  );
};

interface FaultDialogContentProps {
  item: NormalizedFaultEventItem;
}

interface FaultGridData {
  item: NormalizedFaultEventItem;
  faultDescription: ReactNode;
  onRowClick: (arg: { id: string }) => void;
}

const FaultGridRenderer: FC<{ template: object; data: FaultGridData }> = ({ template, data }) => {
  return <>{evaluate(template, data)}</>;
};

const FaultDialogContent: FC<FaultDialogContentProps> = ({ item }) => {
  const { setEvents, setEventIndex, setOpen } = useEventDialog();
  const { loaded, template } = useUiTemplate('history-fault-grid', HISTORY_FAULT_GRID_DEFAULT_TEMPLATE);

  const onRowClick = ({ id }: { id: string }): void => {
    const index = item.originalEvents.findIndex((event) => event.eventId === id);
    if (index !== -1) {
      setEventIndex(index);
      setEvents(item.originalEvents);
      setOpen(true);
    }
  };

  const eventData = item.event.data as unknown as FaultUpdate;
  const faultDescription = (
    <Typography data-testid="fault-description">
      <FaultDescription eventData={eventData} thingId={item.event.thingId} />
    </Typography>
  );

  return (
    <>
      {loaded ? (
        <FaultGridRenderer
          template={template}
          data={{ item, faultDescription, onRowClick }}
        />
      ) : (
        <DialogContent>
          <MiddleSpinner />
        </DialogContent>
      )}
    </>
  );
};
